"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ArrowRight,
  Building2,
  LayoutDashboard,
  LogOut,
  ShieldCheck,
  UserCircle2,
} from "lucide-react";

import { getSupabaseBrowserClient } from "@/lib/auth/supabase-browser";
import { supabaseEnv } from "@/lib/config";

interface AgentSummary {
  slug: string | null;
  status: string;
  isPublic: boolean;
  photoUrl: string | null;
}

type UserState =
  | { kind: "loading" }
  | { kind: "anonymous" }
  | {
      kind: "authed";
      email: string;
      isAdmin: boolean;
      isAgent: boolean;
      agent: AgentSummary | null;
    };

/**
 * Top-right user widget in the site header.
 *
 * - Anonymous → "Crear cuenta" CTA (links to /registro with a
 *   returnTo=/cuenta so the new buyer lands on their dashboard).
 * - Authenticated → circular avatar with first initial; click opens a
 *   dropdown with the email, a "Panel admin" link (admins only), and a
 *   "Cerrar sesión" form post.
 *
 * Auth state is read client-side via the Supabase browser client. On
 * auth-gated routes (/portal/*, /monitoring/*) we suppress the CTA
 * entirely — anyone reaching those routes is already authenticated
 * by middleware, so showing a signup CTA would be confusing. The
 * avatar shows once the client confirms.
 */
export function UserMenu() {
  const pathname = usePathname();
  const isGuardedRoute =
    pathname?.startsWith("/portal") || pathname?.startsWith("/monitoring");

  const [state, setState] = useState<UserState>({ kind: "loading" });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!supabaseEnv.isConfigured) {
      setState({ kind: "anonymous" });
      return;
    }
    let mounted = true;
    const supabase = getSupabaseBrowserClient();

    async function refresh() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!mounted) return;
        if (!user) {
          setState({ kind: "anonymous" });
          return;
        }
        // Pull every role + agent profile summary in parallel. RLS
        // allows the user to read both for themselves.
        const [rolesResult, agentResult] = await Promise.all([
          supabase.from("user_roles").select("role").eq("user_id", user.id),
          supabase
            .from("agents")
            .select("slug, status, is_public, photo_url")
            .eq("user_id", user.id)
            .maybeSingle(),
        ]);
        if (!mounted) return;
        const roles = new Set(
          (rolesResult.data ?? []).map((r: { role: string }) => r.role)
        );
        const agentRow = agentResult.data as
          | {
              slug: string | null;
              status: string;
              is_public: boolean;
              photo_url: string | null;
            }
          | null;
        setState({
          kind: "authed",
          email: user.email ?? "",
          isAdmin: roles.has("admin"),
          isAgent: roles.has("agent"),
          agent: agentRow
            ? {
                slug: agentRow.slug,
                status: agentRow.status,
                isPublic: agentRow.is_public,
                photoUrl: agentRow.photo_url,
              }
            : null,
        });
      } catch (err) {
        // Network blip or auth backend hiccup. Keep showing loading
        // rather than flicker to "Crear cuenta" — onAuthStateChange
        // will re-run refresh on the next session event.
        console.error("[user-menu] auth refresh failed", err);
      }
    }

    refresh();

    // Re-render the menu when the session changes (e.g., after logout
    // posted from another tab or after the magic-link landing).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      refresh();
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div
        aria-hidden
        className="h-8 w-8 animate-pulse rounded-full bg-ink-200/40 sm:h-9 sm:w-9"
      />
    );
  }

  if (state.kind === "anonymous") {
    // Auth-gated routes: middleware already gated entry, so anyone
    // viewing this header IS authenticated. If the client widget
    // can't confirm (env miss, network blip, race with cookie
    // propagation), render nothing rather than a misleading CTA.
    if (isGuardedRoute) return null;
    return (
      <Link
        href="/registro?returnTo=/cuenta"
        className="inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-xs font-semibold text-cream-50 transition hover:bg-coral-600 sm:text-sm"
      >
        Crear cuenta
        <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
      </Link>
    );
  }

  const initial = (state.email[0] ?? "?").toUpperCase();
  const photoUrl = state.agent?.photoUrl ?? null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Menú de cuenta · ${state.email}`}
        className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-coral text-sm font-bold text-cream-50 ring-1 ring-coral/40 transition hover:bg-coral-600 sm:h-9 sm:w-9"
      >
        {photoUrl ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={photoUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          initial
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-30 cursor-default"
          />
          <div
            role="menu"
            className="absolute right-0 z-40 mt-2 w-64 origin-top-right rounded-2xl bg-white p-1 shadow-lg ring-1 ring-ink-200"
          >
            <div className="border-b border-ink-100 px-3 py-2.5">
              <p className="text-xs uppercase tracking-wider text-ink-500">
                Cuenta
              </p>
              <p
                className="mt-0.5 truncate text-sm font-medium text-ink"
                title={state.email}
              >
                {state.email}
              </p>
            </div>
            {state.isAdmin && (
              <Link
                href="/monitoring"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="mt-1 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-ink-700 transition hover:bg-cream-100 hover:text-ink"
              >
                <ShieldCheck className="h-4 w-4 text-coral" aria-hidden />
                Panel admin
              </Link>
            )}
            {(state.isAdmin || state.isAgent) && (
              <Link
                href="/portal"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="mt-1 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-ink-700 transition hover:bg-cream-100 hover:text-ink"
              >
                <LayoutDashboard className="h-4 w-4 text-ink-500" aria-hidden />
                Mi portal
              </Link>
            )}
            {state.isAgent && (
              <Link
                href={
                  state.agent?.slug
                    ? `/agente/${state.agent.slug}`
                    : "/portal/perfil"
                }
                role="menuitem"
                onClick={() => setOpen(false)}
                className="mt-1 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-ink-700 transition hover:bg-cream-100 hover:text-ink"
              >
                <UserCircle2 className="h-4 w-4 text-coral" aria-hidden />
                Mi perfil
              </Link>
            )}
            {/* Buyer surface — show "Mi cuenta" for everyone EXCEPT
                agents (agents already get richer dashboards via
                "Mi portal" + "Mi perfil"). Admins see it too so
                they can preview the buyer experience. */}
            {!state.isAgent && (
              <Link
                href="/cuenta"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="mt-1 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-ink-700 transition hover:bg-cream-100 hover:text-ink"
              >
                <Building2 className="h-4 w-4 text-coral" aria-hidden />
                Mi cuenta
              </Link>
            )}
            <form action="/auth/logout" method="post">
              <button
                type="submit"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-ink-700 transition hover:bg-cream-100 hover:text-ink"
              >
                <LogOut className="h-4 w-4 text-ink-500" aria-hidden />
                Cerrar sesión
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
